import { useEffect, useRef, useState } from 'react';

interface Marco {
  numero: string;
  titulo: string;
  descricao: string;
  icon: string;
}

const marcos: Marco[] = [
  {
    numero: '30',
    titulo: 'Anos de Pânico Superados',
    descricao: 'Três décadas de síndrome do pânico, ansiedade e traumas transformados em liberdade e propósito',
    icon: 'ri-heart-pulse-line',
  },
  {
    numero: '2',
    titulo: 'Filhas Inspiradoras',
    descricao: 'Mãe de duas meninas que me ensinaram o verdadeiro significado de amor incondicional e força',
    icon: 'ri-parent-line',
  },
  {
    numero: '15+',
    titulo: 'Técnicas Dominadas',
    descricao: 'Anos de estudo em PNL, alta performance, meditação e escrita terapêutica',
    icon: 'ri-book-open-line',
  },
  {
    numero: '1',
    titulo: 'Propósito de Vida',
    descricao: 'Transformar minha dor em cura para milhares de pessoas ao redor do mundo',
    icon: 'ri-compass-3-line',
  },
];

const NumerosJornadaPessoal = () => {
  const [visibleCards, setVisibleCards] = useState<number[]>([]);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Staggered reveal
          marcos.forEach((_, index) => {
            setTimeout(() => {
              setVisibleCards((prev) => [...prev, index]);
            }, index * 200);
          });
        }
      },
      { threshold: 0.2 },
    );

    const current = sectionRef.current;
    if (current) observer.observe(current);

    return () => {
      if (current) observer.unobserve(current);
      observer.disconnect();
    };
  }, []);

  return (
    <section ref={sectionRef} className="relative py-32 bg-gradient-to-b from-luxury-black via-luxury-charcoal/20 to-luxury-black overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-28 right-1/4 w-2 h-2 bg-gold-500/30 rounded-full animate-float" />
      <div
        className="absolute top-48 left-1/3 w-1.5 h-1.5 bg-gold-400/20 rounded-full animate-float"
        style={{ animationDelay: '1.5s' }}
      />
      <div
        className="absolute bottom-36 right-1/2 w-1 h-1 bg-gold-300/30 rounded-full animate-float"
        style={{ animationDelay: '3s' }}
      />

      <div className="container mx-auto px-6 lg:px-16 relative z-10">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-20">
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="w-16 h-px bg-gradient-to-r from-transparent to-gold-500/50" />
            <span className="font-['Montserrat'] text-xs tracking-[0.3em] uppercase text-gold-400/70">
              Minha Jornada Pessoal
            </span>
            <div className="w-16 h-px bg-gradient-to-l from-transparent to-gold-500/50" />
          </div>

          <h2 className="font-['Playfair_Display'] text-4xl lg:text-6xl text-white font-light leading-tight mb-6">
            Os números da minha{' '}
            <span className="italic text-gold-400">transformação</span>
          </h2>

          <p className="font-['Montserrat'] text-sm lg:text-base text-white/45 leading-relaxed font-light tracking-wide">
            Cada número conta uma história de superação, aprendizado e renascimento. 
            Esta é a jornada que me trouxe até aqui.
          </p>
        </div>

        {/* Grid de marcos */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
          {marcos.map((marco, index) => {
            const isVisible = visibleCards.includes(index);

            return (
              <div
                key={index}
                className="group relative transition-all duration-700 ease-out"
                style={{
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible
                    ? 'translateY(0) scale(1)'
                    : 'translateY(40px) scale(0.9)',
                  filter: isVisible ? 'blur(0px)' : 'blur(6px)',
                }}
              >
                {/* Card */}
                <div className="relative h-full bg-gradient-to-br from-luxury-charcoal/60 to-luxury-charcoal/40 backdrop-blur-sm border border-gold-500/10 rounded-2xl p-8 hover:border-gold-500/30 transition-all duration-500 hover:shadow-[0_8px_30px_rgba(201,168,76,0.15)] overflow-hidden text-center">
                  {/* Glow effect on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-gold-500/0 to-gold-500/0 group-hover:from-gold-500/5 group-hover:to-gold-500/10 transition-all duration-500 rounded-2xl" />

                  {/* Icon */}
                  <div className="relative mb-6 flex justify-center">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-gold-500/20 to-gold-600/10 border border-gold-500/20 flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-all duration-500">
                      <i className={`${marco.icon} text-3xl text-gold-400 w-8 h-8 flex items-center justify-center`} />
                    </div>
                  </div>

                  {/* Número grande */}
                  <div className="relative mb-4">
                    <h3 className="font-['Playfair_Display'] text-5xl lg:text-6xl text-gold-400 font-bold leading-none">
                      {marco.numero}
                    </h3>
                  </div>

                  {/* Título */}
                  <h4 className="font-['Playfair_Display'] text-xl text-white font-semibold mb-3 leading-tight">
                    {marco.titulo}
                  </h4>

                  {/* Descrição */}
                  <p className="font-['Montserrat'] text-xs text-white/50 leading-relaxed font-light tracking-wide">
                    {marco.descricao}
                  </p>

                  {/* Decorative corner */}
                  <div className="absolute bottom-0 right-0 w-20 h-20 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <div className="absolute bottom-0 right-0 w-full h-full border-b-2 border-r-2 border-gold-500/20 rounded-br-2xl" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Frase inspiracional */}
        <div className="max-w-3xl mx-auto mt-20 text-center">
          <p className="font-['Playfair_Display'] text-xl lg:text-2xl text-white/60 font-light italic leading-relaxed">
            "Cada cicatriz virou sabedoria. Cada lágrima virou força. 
            E hoje, cada história que transformo é a prova de que vale a pena não desistir."
          </p>
          <div className="flex items-center justify-center gap-3 mt-6">
            <div className="w-12 h-px bg-gold-500/40" />
            <span className="font-['Montserrat'] text-xs text-gold-400/70 tracking-wider">
              Bia Holmes
            </span>
            <div className="w-12 h-px bg-gold-500/40" />
          </div>
        </div>

        {/* Bottom decorative line */}
        <div className="flex items-center justify-center gap-4 mt-20">
          <div className="w-24 h-px bg-gradient-to-r from-transparent to-gold-500/30" />
          <i className="ri-heart-line text-gold-500/40 text-xl w-6 h-6 flex items-center justify-center" />
          <div className="w-24 h-px bg-gradient-to-l from-transparent to-gold-500/30" />
        </div>
      </div>
    </section>
  );
};

export default NumerosJornadaPessoal;